import { useCallback, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../store/auth-context";
import { isSupabaseBackend } from "../services/backend";
import {
  disablePushNotifications,
  enablePushNotifications,
  getPushPermission,
  isPushEnabledLocally,
  isPushSupported,
  subscribeToPushNotifications,
} from "../services/pushNotifications";
import {
  isPushConfigured,
  removePushSubscription,
  upsertPushSubscription,
  updatePushPresence,
} from "../services/supabase/pushSubscriptions";
import { updateMyLastSeen } from "../services/supabase/presence";

/**
 * Registers Web Push subscriptions for logged-in users and handles SW navigation.
 * Also heartbeats profile presence so others can see Online / Offline.
 */
export default function PushNotificationManager() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const syncingRef = useRef(false);

  const userId = authCtx.authenticatedUser?.id;

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const onMessage = (event) => {
      if (event.data?.type === "PUSH_NAVIGATE" && event.data.url) {
        navigate(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);

  const syncSubscription = useCallback(async () => {
    if (!userId || !isSupabaseBackend() || !isPushSupported() || !isPushConfigured()) {
      return;
    }
    if (getPushPermission() !== "granted" || !isPushEnabledLocally()) {
      return;
    }
    if (syncingRef.current) return;

    syncingRef.current = true;
    try {
      const subscription = await subscribeToPushNotifications();
      await upsertPushSubscription(userId, subscription, navigator.userAgent);
    } catch (error) {
      console.warn("Could not sync push subscription:", error);
    } finally {
      syncingRef.current = false;
    }
  }, [userId]);

  useEffect(() => {
    syncSubscription();
  }, [syncSubscription]);

  // Presence + push heartbeat while the app is visible
  useEffect(() => {
    if (!userId || !isSupabaseBackend()) return undefined;
    if (typeof document === "undefined") return undefined;

    let intervalId = null;

    const pingPresence = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        await updateMyLastSeen(userId);
      } catch {
        /* ignore */
      }

      if (!isPushConfigured()) return;
      try {
        const registration = await navigator.serviceWorker?.ready;
        const subscription = await registration?.pushManager?.getSubscription();
        if (subscription?.endpoint) {
          await updatePushPresence(userId, subscription.endpoint);
        }
      } catch {
        /* ignore presence errors */
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        pingPresence();
      }
    };

    pingPresence();
    intervalId = window.setInterval(pingPresence, 25_000);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [userId]);

  return null;
}

export async function togglePushNotifications(userId, enabled) {
  if (!userId || !isPushSupported() || !isPushConfigured()) {
    return { ok: false };
  }

  if (enabled) {
    return enablePushNotifications(userId, upsertPushSubscription);
  }

  return disablePushNotifications(userId, removePushSubscription);
}

export function pushNotificationsAvailable() {
  return isPushSupported() && isPushConfigured() && isSupabaseBackend();
}
